import { FC, ReactNode, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';

interface AdminLayoutProps {
  children: ReactNode;
}

export const AdminLayout: FC<AdminLayoutProps> = ({ children }) => {
  const { user, isLoading, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const closeMobileSidebar = () => setMobileSidebarOpen(false);
  const toggleMobileSidebar = () => setMobileSidebarOpen((prev) => !prev);

  // Check if user has admin role (ADMIN or SUPER_ADMIN)
  const hasAdminAccess = isAdmin();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-slate-900">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-slate-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Show error if not admin
  if (!hasAdminAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-slate-900 p-4">
        <div className="max-w-md w-full p-8 bg-white dark:bg-slate-800 rounded-lg shadow text-center">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">
            You do not have permission to access the admin dashboard. Please contact an
            administrator if you believe this is an error.
          </p>
          <a
            href="/dashboard"
            className="inline-block w-full px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Go to User Dashboard
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-slate-900">
      {/* Mobile hamburger toggle */}
      <button
        onClick={toggleMobileSidebar}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-slate-800 rounded-lg shadow-md hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
        aria-label={mobileSidebarOpen ? 'Close admin menu' : 'Open admin menu'}
        aria-expanded={mobileSidebarOpen}
        aria-controls="admin-sidebar"
      >
        {mobileSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar */}
      <AdminSidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        isMobileOpen={mobileSidebarOpen}
        onMobileClose={closeMobileSidebar}
      />

      {/* Main Content */}
      <main
        className={cn(
          'min-h-screen transition-all duration-300',
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        )}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 flex items-center justify-between">
          <p className="text-lg font-semibold text-gray-900 dark:text-slate-100 pl-10 lg:pl-0">Admin Dashboard</p>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 dark:text-slate-400 hidden sm:inline">
              Logged in as <span className="font-medium text-gray-900 dark:text-slate-100">{user?.name}</span>
            </span>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {errorMessage && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-red-800">{errorMessage}</p>
              <button
                onClick={() => setErrorMessage(null)}
                className="mt-2 text-sm text-red-600 hover:text-red-800"
              >
                Dismiss
              </button>
            </div>
          )}
          {children}
        </div>
      </main>

      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden transition-opacity"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
